// QualityReportCard — the hero of a hospital's Quality tab (2026-09-15).
//
// One patriotic "report card" that puts every quality signal we hold for the
// hospital in one frame: CMS overall stars + the three CMS domain ratings,
// HCAHPS (would-recommend %, summary star, 9–10 rating %, per-domain stars),
// ER wait vs the U.S. median, hospital-acquired-infection SIRs, Google, the
// verdict tallies across every CMS Care Compare measure the hospital reports,
// and the price grade — plus a data-generated paragraph that reads the card
// out in plain English. That paragraph is deliberately unique per hospital
// (every clause is driven by this hospital's own numbers) so the tab carries
// real text for readers and crawlers, not a shared boilerplate intro.
//
// Left column is Dignity's briefing slot: the per-hospital video when one
// exists, otherwise the GLB with the "Guide me" narration trigger (which
// plays the hospital-quality narration lines via DignityNarrationPlayer).
//
// Data: `hospital` meta (static JSON / hospitals row), the Care Compare
// measures the section already fetches, and one extra query here against
// hospital_cms_data for the HCAHPS / timely-care / HAI rows that live only
// in that table.
import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Activity, AlertTriangle, Banknote, Clock, Heart, Play, ShieldCheck, Star, Stethoscope, Users, Video } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { ensureModelViewer } from "@/lib/ensureModelViewer";
import { getHospitalGradeInfo } from "@/lib/grades";
import { titleCaseHospital } from "@/lib/hospitalName";
import { briefingPoster, type HospitalBriefingVideo } from "@/hooks/useHospitalBriefingVideo";

export type QualityTone = "good" | "neutral" | "bad" | "na";

export interface QualityMeasureRow {
  measure_id: string;
  measure_name: string | null;
  compared_to_national: string | null;
  score: number | string | null;
  denominator: number | string | null;
  period_end: string | null;
}

export interface QualityGroupTally {
  key: string;
  label: string;
  better: number;
  same: number;
  worse: number;
  na: number;
}

export interface QualityReportCardProps {
  hospital: {
    id: string;
    name: string;
    city?: string | null;
    state?: string | null;
    cms_overall_rating?: number | string | null;
    cms_mortality_rating?: string | null;
    cms_safety_rating?: string | null;
    cms_readmission_rating?: string | null;
    google_rating?: number | string | null;
    google_review_count?: number | string | null;
    cms_certification_number?: string | null;
    ccn?: string | null;
    // Added to the static meta by generate_hospital_detail_cache.py; used
    // when present so the card doesn't wait on the runtime CMS query.
    cms_as_of_date?: string | null;
    hcahps_recommend_pct?: number | string | null;
    hcahps_surveys?: number | string | null;
    data_vintage_year?: number | string | null;
  };
  measures: QualityMeasureRow[];
  measuresLoading: boolean;
  /** Per-group verdict tallies computed by the section (same bucketing the
   *  slider cards below use). */
  tallies: QualityGroupTally[];
  /** National median for a measure id, from national_quality_index. */
  nationalMedian: (measureId: string) => number | null;
  /** Price side of the card — the same grade the rankings and map show. */
  price: { markup: number | null; rank: number | null; totalRanked: number | null };
  /** Year of the price file behind the price grade — stamped beside the
   *  CMS release date. Dates on this card are stamps, never filters. */
  dataVintageYear?: number | null;
  isDark: boolean;
  /** Per-hospital Dignity briefing video (see useHospitalBriefingVideo).
   *  When present the slot shows its poster with a play control that opens
   *  the page's bottom-right PiP player; otherwise the GLB. */
  briefing?: HospitalBriefingVideo | null;
  onPlayBriefing?: () => void;
  onGuideMe: () => void;
  /** The page's DignityNarrationPlayer (compact trigger) — rendered at the
   *  foot of Dignity's column so the guide lives with the guide. */
  narration?: React.ReactNode;
}

// hospital_cms_data rows we read for the card. HCAHPS star/percent codes
// carry their values in the payload's "Patient Survey Star Rating" /
// "HCAHPS Answer Percent"; timely-care + HAI rows carry "Score".
const HCAHPS_DOMAIN_STARS: { code: string; key: string; label: string }[] = [
  { code: "H_COMP_1_STAR_RATING", key: "nurses",     label: "Nurse communication" },
  { code: "H_COMP_2_STAR_RATING", key: "doctors",    label: "Doctor communication" },
  { code: "H_COMP_3_STAR_RATING", key: "staff",      label: "Staff responsiveness" },
  { code: "H_COMP_5_STAR_RATING", key: "medicines",  label: "Explaining medicines" },
  { code: "H_COMP_6_STAR_RATING", key: "discharge",  label: "Discharge information" },
  { code: "H_COMP_7_STAR_RATING", key: "transition", label: "Care transition" },
  { code: "H_CLEAN_STAR_RATING",  key: "clean",      label: "Cleanliness" },
  { code: "H_QUIET_STAR_RATING",  key: "quiet",      label: "Quiet at night" },
];
const HAI_CODES: { code: string; label: string }[] = [
  { code: "HAI_1_SIR", label: "Central-line bloodstream infections" },
  { code: "HAI_2_SIR", label: "Catheter urinary tract infections" },
  { code: "HAI_3_SIR", label: "Surgical site infections — colon" },
  { code: "HAI_4_SIR", label: "Surgical site infections — hysterectomy" },
  { code: "HAI_5_SIR", label: "MRSA bloodstream infections" },
  { code: "HAI_6_SIR", label: "C. diff infections" },
];
const CMS_SNAPSHOT_CODES = [
  "H_RECMND_DY", "H_STAR_RATING", "H_HSP_RATING_9_10",
  ...HCAHPS_DOMAIN_STARS.map((d) => d.code),
  "OP_18b", "OP_22",
  ...HAI_CODES.map((h) => h.code),
];

type CmsRow = { dataset: string; measure_code: string | null; payload: unknown; as_of_date: string | null };

function payloadOf(row: CmsRow): Record<string, unknown> {
  const p = row.payload;
  if (p && typeof p === "object") return p as Record<string, unknown>;
  if (typeof p === "string") { try { return JSON.parse(p); } catch { return {}; } }
  return {};
}
function num(v: unknown): number | null {
  if (v == null) return null;
  const n = Number(String(v).replace(/,/g, ""));
  return Number.isFinite(n) ? n : null;
}

// CMS phrases → tone. Covers the star-rating domain strings ("Above/Same
// as/Below the national average") and the measure comparisons ("Better
// Than/No Different Than/Worse Than the National Rate|Benchmark").
export function toneOf(cmp: string | null | undefined): QualityTone {
  if (!cmp) return "na";
  const s = cmp.toLowerCase();
  if (s.includes("better") || s.includes("above") || s.includes("fewer days")) return "good";
  if (s.includes("worse") || s.includes("below") || s.includes("more days")) return "bad";
  if (s.includes("no different") || s.includes("same") || s.includes("average")) return "neutral";
  return "na";
}

function StarRow({ value, size = 14, color = "var(--civic-gold)", muted }: { value: number | null; size?: number; color?: string; muted: string }) {
  return (
    <span className="inline-flex items-center gap-[2px]" aria-hidden>
      {[1, 2, 3, 4, 5].map((i) => (
        <Star
          key={i}
          style={{ width: size, height: size }}
          fill={value != null && i <= Math.round(value) ? color : "transparent"}
          stroke={value != null && i <= Math.round(value) ? color : muted}
          strokeWidth={1.75}
        />
      ))}
    </span>
  );
}

export function QualityReportCard({
  hospital, measures, measuresLoading, tallies, nationalMedian, price, dataVintageYear, isDark, briefing, onPlayBriefing, onGuideMe, narration,
}: QualityReportCardProps) {
  const { t } = useTranslation(["hospital"]);
  const tr = (key: string, fallback: string, opts?: Record<string, unknown>) =>
    t(`hospital:quality.reportCard.${key}`, { defaultValue: fallback, ...(opts || {}) });

  // ── Palette (mirrors the StatCard / QualityMeasureSlider civic tokens) ──
  const P = isDark ? {
    cardBg: "linear-gradient(180deg, rgba(2, 22, 60, 0.94) 0%, rgba(0, 14, 44, 0.97) 100%)",
    cardBorder: "rgba(var(--civic-gold-rgb), 0.22)",
    cardShadow: "0 1px 0 rgba(255,255,255,0.04) inset, 0 16px 40px -24px rgba(0,0,0,0.7)",
    text: "var(--civic-parchment)",
    muted: "rgba(var(--civic-parchment-rgb), 0.62)",
    faded: "rgba(var(--civic-parchment-rgb), 0.42)",
    divider: "rgba(var(--civic-gold-rgb), 0.18)",
    tileBg: "rgba(var(--civic-parchment-rgb), 0.04)",
    tileBorder: "rgba(var(--civic-gold-rgb), 0.16)",
    asideBg: "rgba(var(--civic-navy-deeper-rgb), 0.55)",
    gold: "var(--civic-gold)",
    goldInk: "var(--civic-gold)",
    navy: "var(--civic-parchment)",
    red: "var(--civic-red-soft, #E4566A)",
    tones: {
      good:    { fg: "#86efac", bg: "rgba(16,185,129,0.16)",  bd: "rgba(16,185,129,0.50)",  bar: "#10b981" },
      neutral: { fg: "var(--civic-parchment)", bg: "rgba(var(--civic-gold-rgb),0.18)", bd: "rgba(var(--civic-gold-rgb),0.60)", bar: "var(--civic-gold)" },
      bad:     { fg: "#FCA5A5", bg: "rgba(var(--civic-red-rgb),0.20)", bd: "rgba(var(--civic-red-rgb),0.60)", bar: "var(--civic-red)" },
      na:      { fg: "#D4C9AE", bg: "rgba(var(--civic-parchment-rgb),0.08)", bd: "rgba(var(--civic-parchment-rgb),0.25)", bar: "#7B8AA0" },
    },
  } : {
    cardBg: "linear-gradient(180deg, #FFFFFF 0%, #FBF7EE 100%)",
    cardBorder: "rgba(var(--civic-navy-deeper-rgb), 0.14)",
    cardShadow: "0 1px 0 rgba(255,255,255,0.6) inset, 0 10px 28px -18px rgba(var(--civic-navy-deeper-rgb), 0.25)",
    text: "var(--civic-navy-deeper)",
    muted: "rgba(var(--civic-navy-deeper-rgb), 0.66)",
    faded: "rgba(var(--civic-navy-deeper-rgb), 0.5)",
    divider: "rgba(var(--civic-gold-rgb), 0.35)",
    tileBg: "rgba(255,255,255,0.7)",
    tileBorder: "rgba(var(--civic-navy-deeper-rgb), 0.12)",
    asideBg: "rgba(var(--civic-navy-rgb), 0.05)",
    gold: "var(--civic-gold)",
    goldInk: "var(--civic-gold-ink)",
    navy: "var(--civic-navy-deeper)",
    red: "var(--civic-red)",
    tones: {
      good:    { fg: "#0F6B49", bg: "rgba(15,138,95,0.12)",   bd: "rgba(15,138,95,0.45)",   bar: "#0F8A5F" },
      neutral: { fg: "var(--civic-gold-ink)", bg: "rgba(var(--civic-gold-rgb),0.18)", bd: "rgba(var(--civic-gold-rgb),0.65)", bar: "#C5A55A" },
      bad:     { fg: "#8B1828", bg: "rgba(var(--civic-red-rgb),0.12)", bd: "rgba(var(--civic-red-rgb),0.55)", bar: "var(--civic-red)" },
      na:      { fg: "#475569", bg: "rgba(var(--civic-navy-deeper-rgb),0.06)", bd: "rgba(var(--civic-navy-deeper-rgb),0.20)", bar: "#94A3B8" },
    },
  };
  const serif = "'Instrument Serif', Georgia, serif";
  const mono = "ui-monospace, SFMono-Regular, Menlo, monospace";

  // ── Dignity slot: model-viewer loads lazily, only when this card mounts ──
  const modelViewerRef = useRef<HTMLElement>(null);
  const [modelViewerReady, setModelViewerReady] = useState(false);
  useEffect(() => {
    if (briefing) return;
    if (typeof customElements !== "undefined" && customElements.get("model-viewer")) { setModelViewerReady(true); return; }
    ensureModelViewer().then(() => setModelViewerReady(true)).catch(() => {});
  }, [briefing]);
  const briefingPosterUrl = briefing ? briefingPoster(briefing) : null;

  // ── HCAHPS / timely care / HAI snapshot ─────────────────────────────────
  const { data: cmsRows } = useQuery({
    queryKey: ["hospital-cms-snapshot", hospital.id],
    enabled: !!hospital.id,
    staleTime: 1000 * 60 * 60,
    refetchOnWindowFocus: false,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("hospital_cms_data")
        .select("dataset, measure_code, payload, as_of_date")
        .eq("hospital_id", hospital.id)
        // The 'general' dataset row carries CMS's release stamp (its
        // as_of_date is the release, not a measurement period).
        .or(`measure_code.in.(${CMS_SNAPSHOT_CODES.join(",")}),dataset.eq.general`);
      if (error) throw error;
      return (data || []) as CmsRow[];
    },
  });

  const snap = useMemo(() => {
    const byCode = new Map<string, CmsRow>();
    for (const r of cmsRows || []) {
      if (!r.measure_code) continue;
      const prev = byCode.get(r.measure_code);
      if (!prev || (r.as_of_date || "") > (prev.as_of_date || "")) byCode.set(r.measure_code, r);
    }
    const pl = (code: string) => { const r = byCode.get(code); return r ? payloadOf(r) : null; };
    const recmnd = pl("H_RECMND_DY");
    const star = pl("H_STAR_RATING");
    const r910 = pl("H_HSP_RATING_9_10");
    const er = pl("OP_18b");
    const left = pl("OP_22");
    const hai = HAI_CODES.map((h) => {
      const p = pl(h.code);
      return p ? { ...h, sir: num(p["Score"]), cmp: (p["Compared to National"] as string | null) ?? null, name: (p["Measure Name"] as string) || h.label } : null;
    }).filter((x): x is NonNullable<typeof x> => !!x && x.sir != null);
    const domains = HCAHPS_DOMAIN_STARS.map((d) => {
      const p = pl(d.code);
      const v = p ? num(p["Patient Survey Star Rating"]) : null;
      return v != null ? { ...d, stars: v } : null;
    }).filter((x): x is NonNullable<typeof x> => !!x);
    // as_of_date is the measurement period end for scored rows; the newest
    // one across every dataset row is CMS's release stamp for the hospital.
    let asOf: string | null = null;
    for (const r of cmsRows || []) if (r.as_of_date && (!asOf || r.as_of_date > asOf)) asOf = r.as_of_date;
    let periodEnd: string | null = null;
    for (const r of byCode.values()) if (r.as_of_date && (!periodEnd || r.as_of_date > periodEnd)) periodEnd = r.as_of_date;
    return {
      cmsAsOf: asOf,
      snapshotPeriodEnd: periodEnd,
      recommendPct: recmnd ? num(recmnd["HCAHPS Answer Percent"]) : (num(hospital.hcahps_recommend_pct)),
      surveys: recmnd ? num(recmnd["Number of Completed Surveys"]) : (num(hospital.hcahps_surveys)),
      responseRate: recmnd ? num(recmnd["Survey Response Rate Percent"]) : null,
      summaryStar: star ? num(star["Patient Survey Star Rating"]) : null,
      rating910Pct: r910 ? num(r910["HCAHPS Answer Percent"]) : null,
      erWaitMin: er ? num(er["Score"]) : null,
      leftBeforeSeenPct: left ? num(left["Score"]) : null,
      hai,
      domains,
    };
  }, [cmsRows, hospital.hcahps_recommend_pct, hospital.hcahps_surveys]);

  // ── Derived headline numbers ────────────────────────────────────────────
  const cmsStars = num(hospital.cms_overall_rating);
  const google = num(hospital.google_rating);
  const googleN = num(hospital.google_review_count);
  const erNational = nationalMedian("OP_18b");
  const erTone: QualityTone = snap.erWaitMin == null || erNational == null ? "na"
    : snap.erWaitMin <= erNational * 0.9 ? "good" : snap.erWaitMin >= erNational * 1.1 ? "bad" : "neutral";
  const haiTally = snap.hai.reduce((a, h) => { a[toneOf(h.cmp)]++; return a; }, { good: 0, neutral: 0, bad: 0, na: 0 } as Record<QualityTone, number>);
  const measureTotals = tallies.reduce((a, g) => ({ better: a.better + g.better, same: a.same + g.same, worse: a.worse + g.worse, na: a.na + g.na }), { better: 0, same: 0, worse: 0, na: 0 });
  const measuresCounted = measureTotals.better + measureTotals.same + measureTotals.worse;
  const gi = price.markup != null ? getHospitalGradeInfo(price.markup) : null;
  const gradeLetter = gi ? String(gi.grade) : null;
  // Date-only strings ("2025-06-30") parse as UTC midnight and print a day
  // early west of Greenwich — pin them to local time before formatting.
  const fmtDate = (d: string | null | undefined) => d
    ? new Date(/^\d{4}-\d{2}-\d{2}$/.test(d) ? `${d}T00:00:00` : d).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })
    : null;
  // Two stamps every reader should see: when CMS released the numbers, and
  // which year's price file the price grade comes from. A third, quieter one
  // says what period the scored measures cover.
  const cmsAsOf = hospital.cms_as_of_date || snap.cmsAsOf || null;
  const periodEnd = useMemo(() => {
    let best: string | null = snap.snapshotPeriodEnd;
    for (const m of measures) if (m.period_end && (!best || m.period_end > best)) best = m.period_end;
    return best;
  }, [measures, snap.snapshotPeriodEnd]);
  const cmsAsOfLabel = fmtDate(cmsAsOf);
  const periodLabel = fmtDate(periodEnd);
  const vintage = dataVintageYear ?? num(hospital.data_vintage_year);
  const ccn = hospital.cms_certification_number || hospital.ccn || null;
  // Static meta ships names/cities upper-cased ("MORRISTOWN MEDICAL CENTER");
  // read them the way the hero does.
  const displayName = titleCaseHospital(hospital.name) || hospital.name;
  const place = [hospital.city ? titleCaseHospital(hospital.city) : null, hospital.state].filter(Boolean).join(", ");

  const domainPhrase = (v: string | null | undefined) => {
    const tone = toneOf(v);
    if (tone === "good") return tr("phrase.above", "above the national average");
    if (tone === "bad") return tr("phrase.below", "below the national average");
    if (tone === "neutral") return tr("phrase.same", "the same as the national average");
    return null;
  };
  const domainLabel = (v: string | null | undefined) => {
    const tone = toneOf(v);
    if (tone === "good") return tr("verdict.above", "Above U.S. average");
    if (tone === "bad") return tr("verdict.below", "Below U.S. average");
    if (tone === "neutral") return tr("verdict.same", "Same as U.S. average");
    return tr("notPublished", "CMS has not published this");
  };

  // ── The unique paragraph: every clause keyed to this hospital's numbers ──
  const summary = useMemo(() => {
    const parts: string[] = [];
    const nm = displayName;
    if (cmsStars != null) {
      const note = cmsStars >= 5 ? tr("summary.topMark", " — the highest mark CMS gives")
        : cmsStars <= 1 ? tr("summary.lowestMark", " — the lowest mark CMS gives") : "";
      parts.push(tr("summary.stars", "{{name}} in {{place}} holds a {{stars}}-star CMS overall rating{{note}}.", { name: nm, place, stars: cmsStars, note }));
    } else {
      parts.push(tr("summary.noStars", "CMS has not published an overall star rating for {{name}} in {{place}}.", { name: nm, place }));
    }
    // Group the three CMS domains by verdict so "mortality above, safety
    // above, readmissions above" collapses into one clause.
    const domainList = (items: string[]) => items.length > 2 ? `${items.slice(0, -1).join(", ")}, and ${items[items.length - 1]}` : items.join(" and ");
    const domainsByPhrase = new Map<string, string[]>();
    ([
      [hospital.cms_mortality_rating, tr("summary.domainMortality", "mortality")],
      [hospital.cms_safety_rating, tr("summary.domainSafety", "safety of care")],
      [hospital.cms_readmission_rating, tr("summary.domainReadmission", "readmissions")],
    ] as const).forEach(([rating, label]) => {
      const ph = domainPhrase(rating);
      if (!ph) return;
      domainsByPhrase.set(ph, [...(domainsByPhrase.get(ph) || []), label]);
    });
    if (domainsByPhrase.size) {
      const clauses = [...domainsByPhrase.entries()].map(([ph, labels]) => `${domainList(labels)} ${ph}`);
      parts.push(tr("summary.domains", "Medicare rates its {{list}}.", { list: clauses.join("; ") }));
    }
    if (measuresCounted > 0) {
      parts.push(tr("summary.measures", "Across {{n}} CMS Care Compare measures with a verdict, {{better}} rate better than the national benchmark, {{same}} about the same, and {{worse}} worse.", {
        n: measuresCounted,
        better: measureTotals.better === 0 ? tr("summary.none", "none") : measureTotals.better,
        same: measureTotals.same,
        worse: measureTotals.worse === 0 ? tr("summary.none", "none") : measureTotals.worse,
      }));
    }
    if (snap.recommendPct != null) {
      parts.push(snap.summaryStar != null
        ? tr("summary.hcahpsWithStar", "{{pct}}% of {{n}} surveyed patients said they would definitely recommend it, and its patient-survey summary rating is {{star}} of 5 stars.", { pct: snap.recommendPct, n: (snap.surveys ?? 0).toLocaleString(), star: snap.summaryStar })
        : tr("summary.hcahps", "{{pct}}% of {{n}} surveyed patients said they would definitely recommend it.", { pct: snap.recommendPct, n: (snap.surveys ?? 0).toLocaleString() }));
    }
    if (snap.erWaitMin != null) {
      const cmp = erNational == null ? "" : erTone === "good" ? tr("summary.erShorter", ", shorter than the U.S. median of {{nat}} minutes", { nat: Math.round(erNational) })
        : erTone === "bad" ? tr("summary.erLonger", ", longer than the U.S. median of {{nat}} minutes", { nat: Math.round(erNational) })
        : tr("summary.erSame", ", about the U.S. median of {{nat}} minutes", { nat: Math.round(erNational) });
      parts.push(tr("summary.er", "Emergency patients spent a median {{min}} minutes in its ER{{cmp}}.", { min: Math.round(snap.erWaitMin), cmp }));
    }
    if (snap.hai.length) {
      parts.push(tr("summary.hai", "Of {{n}} hospital-acquired-infection measures, {{better}} are better than the national benchmark and {{worse}} worse.", { n: snap.hai.length, better: haiTally.good === 0 ? tr("summary.none", "none") : haiTally.good, worse: haiTally.bad === 0 ? tr("summary.none", "none") : haiTally.bad }));
    }
    if (google != null) {
      parts.push(tr("summary.google", "Patients on Google rate it {{g}} out of 5 across {{n}} reviews.", { g: google.toFixed(1), n: (googleN ?? 0).toLocaleString() }));
    }
    if (gradeLetter && price.markup != null) {
      const pct = `${price.markup >= 0 ? "+" : "−"}${Math.abs(Math.round(price.markup)).toLocaleString()}%`;
      parts.push(price.rank != null && price.totalRanked
        ? tr("summary.priceRanked", "Its price grade is {{grade}}: published charges run {{pct}} against the national median, ranked #{{rank}} of {{total}} hospitals — a separate scale from quality.", { grade: gradeLetter, pct, rank: price.rank.toLocaleString(), total: price.totalRanked.toLocaleString() })
        : tr("summary.price", "Its price grade is {{grade}}: published charges run {{pct}} against the national median — a separate scale from quality.", { grade: gradeLetter, pct }));
    }
    return parts.join(" ");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [displayName, place, cmsStars, hospital.cms_mortality_rating, hospital.cms_safety_rating, hospital.cms_readmission_rating, measuresCounted, measureTotals.better, measureTotals.same, measureTotals.worse, snap, erNational, erTone, haiTally.good, haiTally.bad, google, googleN, gradeLetter, price.markup, price.rank, price.totalRanked]);

  // ── Tiles ───────────────────────────────────────────────────────────────
  const Tile = ({ icon, label, value, unit, sub, tone = "na", title }: {
    icon: React.ReactNode; label: string; value: React.ReactNode; unit?: string; sub?: React.ReactNode; tone?: QualityTone; title?: string;
  }) => {
    const c = P.tones[tone];
    return (
      <div
        title={title}
        style={{
          position: "relative", padding: "12px 14px 12px 16px", borderRadius: 8,
          background: P.tileBg, border: `1px solid ${P.tileBorder}`, minWidth: 0,
        }}
      >
        <span aria-hidden style={{ position: "absolute", left: 0, top: 10, bottom: 10, width: 3, borderRadius: 999, background: c.bar, boxShadow: `0 0 8px ${c.bar}55` }} />
        <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 10, fontWeight: 800, letterSpacing: "0.10em", textTransform: "uppercase", color: P.muted }}>
          <span style={{ color: P.goldInk, display: "inline-flex" }}>{icon}</span>{label}
        </div>
        <div style={{ display: "flex", alignItems: "baseline", gap: 6, marginTop: 6, color: P.text }}>
          <span style={{ fontFamily: serif, fontSize: 30, lineHeight: 1, fontWeight: 400 }}>{value}</span>
          {unit && <span style={{ fontSize: 12, color: P.muted }}>{unit}</span>}
        </div>
        {sub && <div style={{ fontSize: 11, color: P.faded, marginTop: 5, lineHeight: 1.35 }}>{sub}</div>}
      </div>
    );
  };
  const Chip = ({ label, tone, value }: { label: string; tone: QualityTone; value: string }) => {
    const c = P.tones[tone];
    return (
      <span style={{ display: "inline-flex", flexDirection: "column", gap: 2, padding: "6px 10px", borderRadius: 6, background: c.bg, border: `1px solid ${c.bd}`, minWidth: 0 }}>
        <span style={{ fontSize: 9.5, fontWeight: 800, letterSpacing: "0.10em", textTransform: "uppercase", color: P.muted }}>{label}</span>
        <span style={{ fontSize: 12, fontWeight: 700, color: c.fg, whiteSpace: "nowrap" }}>{value}</span>
      </span>
    );
  };
  const tallyBar = (g: QualityGroupTally) => {
    const n = g.better + g.same + g.worse;
    if (n === 0) return null;
    const w = (x: number) => `${(x / n) * 100}%`;
    return (
      <div key={g.key} style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr) 150px auto", alignItems: "center", columnGap: 10, fontSize: 12 }}>
        <span style={{ color: P.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{g.label}</span>
        <span style={{ display: "flex", height: 8, borderRadius: 999, overflow: "hidden", background: P.tones.na.bg }} aria-hidden>
          <span style={{ width: w(g.better), background: P.tones.good.bar }} />
          <span style={{ width: w(g.same), background: P.tones.neutral.bar }} />
          <span style={{ width: w(g.worse), background: P.tones.bad.bar }} />
        </span>
        <span style={{ fontFamily: mono, fontSize: 11, color: P.muted, whiteSpace: "nowrap" }}>
          <span style={{ color: P.tones.good.fg, fontWeight: 700 }}>{g.better}</span> · <span style={{ color: P.tones.neutral.fg, fontWeight: 700 }}>{g.same}</span> · <span style={{ color: P.tones.bad.fg, fontWeight: 700 }}>{g.worse}</span>
        </span>
      </div>
    );
  };

  return (
    <section
      aria-label={tr("aria", "Quality report card for {{name}}", { name: displayName })}
      style={{ background: P.cardBg, border: `1px solid ${P.cardBorder}`, boxShadow: P.cardShadow, borderRadius: 10, overflow: "hidden", color: P.text }}
    >
      {/* Flag-stripe crown */}
      <div aria-hidden style={{ height: 4, background: "linear-gradient(90deg, var(--civic-red) 0 33%, var(--civic-parchment, #F4EEDC) 33% 66%, var(--civic-navy) 66%)" }} />

      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, padding: "12px 18px 0", flexWrap: "wrap" }}>
        <div style={{ display: "inline-flex", alignItems: "center", gap: 8, fontSize: 10.5, fontWeight: 800, letterSpacing: "0.14em", textTransform: "uppercase", color: P.goldInk }}>
          <ShieldCheck style={{ width: 14, height: 14 }} />
          {tr("eyebrow", "Report card · CMS Care Compare · HCAHPS · Google")}
        </div>
        <div style={{ fontSize: 11, color: P.faded, fontFamily: mono, display: "flex", flexWrap: "wrap", gap: "2px 10px", justifyContent: "flex-end" }}>
          <span title={tr("stamps.cmsTitle", "When CMS released the quality data shown here")}>
            {cmsAsOfLabel ? tr("stamps.cms", "CMS data as of {{date}}", { date: cmsAsOfLabel }) : tr("stamps.cmsPending", "CMS release date pending")}
          </span>
          {periodLabel && periodLabel !== cmsAsOfLabel && (
            <span title={tr("stamps.periodTitle", "End of the reporting period the scored measures cover")}>
              {tr("stamps.period", "measures through {{date}}", { date: periodLabel })}
            </span>
          )}
          <span title={tr("stamps.vintageTitle", "Year of the hospital's published price file our price grade is computed from")}>
            {vintage != null ? tr("stamps.vintage", "prices from the hospital's {{year}} file", { year: vintage }) : tr("stamps.vintagePending", "price-file year pending")}
          </span>
          {ccn ? <span>CCN {ccn}</span> : null}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-[260px_1fr]" style={{ gap: 0 }}>
        {/* ── Dignity briefing slot ─────────────────────────────────────── */}
        <aside style={{ background: P.asideBg, borderRight: `1px solid ${P.divider}`, margin: "12px 0 0", display: "flex", flexDirection: "column" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 6, padding: "10px 14px", borderBottom: `1px solid ${P.divider}` }}>
            <span style={{ fontSize: 10, fontWeight: 800, letterSpacing: "0.12em", textTransform: "uppercase", color: P.goldInk }}>{tr("dignity", "Dignity")}</span>
            <span style={{ fontSize: 10, color: P.faded }}>{tr("briefing", "— hospital briefing")}</span>
          </div>
          <div style={{ position: "relative", aspectRatio: "16 / 11", minHeight: 170 }}>
            {briefing ? (
              <button
                type="button"
                onClick={onPlayBriefing}
                aria-label={tr("playBriefing", "Play Dignity's briefing on {{name}}", { name: displayName })}
                title={tr("playBriefing", "Play Dignity's briefing on {{name}}", { name: displayName })}
                style={{ position: "absolute", inset: 0, width: "100%", height: "100%", border: 0, padding: 0, cursor: "pointer", background: "#0a1628", overflow: "hidden" }}
              >
                {briefingPosterUrl ? (
                  <img src={briefingPosterUrl} alt="" style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover", opacity: 0.85 }} />
                ) : (
                  <img src="/consumer/dignity-transparent.webp" alt="" style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "contain", padding: 8 }} />
                )}
                <span style={{ position: "absolute", inset: 0, background: "radial-gradient(ellipse at center, rgba(0,0,0,0) 35%, rgba(0,20,60,0.55) 100%)" }} aria-hidden />
                <span style={{ position: "absolute", left: "50%", top: "50%", transform: "translate(-50%, -50%)", width: 52, height: 52, borderRadius: "50%", background: "var(--civic-gold)", display: "inline-flex", alignItems: "center", justifyContent: "center", boxShadow: "0 8px 24px rgba(0,0,0,0.45)" }}>
                  <Play style={{ width: 20, height: 20, marginLeft: 3, color: "#0a1628" }} fill="currentColor" />
                </span>
                <span style={{ position: "absolute", left: 10, right: 10, bottom: 8, fontSize: 10.5, fontWeight: 700, color: "#FAF9F6", textShadow: "0 1px 3px rgba(0,0,0,0.7)", textAlign: "left" }}>
                  {tr("playBriefingLabel", "Watch Dignity's briefing")}{briefing.durationSec ? ` · ${Math.floor(briefing.durationSec / 60)}:${String(Math.round(briefing.durationSec % 60)).padStart(2, "0")}` : ""}
                </span>
              </button>
            ) : modelViewerReady ? (
              /* @ts-ignore - model-viewer custom element */
              <model-viewer
                ref={modelViewerRef}
                src="/consumer/dignity_bill.glb"
                alt="Dignity — Your Healthcare Guide"
                shadow-intensity="0.5"
                exposure="1.1"
                interaction-prompt="none"
                camera-orbit="0deg 75deg 115%"
                style={{ width: "100%", height: "100%", pointerEvents: "none" }}
              />
            ) : (
              <img src="/consumer/dignity-transparent.webp" alt="Dignity" style={{ width: "100%", height: "100%", objectFit: "contain", padding: 8 }} />
            )}
          </div>
          <button
            type="button"
            onClick={onGuideMe}
            className="hover:opacity-90 transition-opacity"
            style={{
              display: "flex", alignItems: "center", gap: 10, padding: "10px 14px", cursor: "pointer", textAlign: "left",
              background: isDark ? "rgba(var(--civic-red-rgb), 0.18)" : "rgba(var(--civic-red-rgb), 0.10)", border: 0, borderTop: `1px solid ${P.divider}`, color: P.text,
            }}
          >
            <span style={{ width: 30, height: 30, borderRadius: "50%", background: "var(--civic-gold)", display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
              <Play style={{ width: 13, height: 13, marginLeft: 2, color: "#0a1628" }} fill="currentColor" />
            </span>
            <span style={{ minWidth: 0 }}>
              <span style={{ display: "block", fontSize: 12.5, fontWeight: 700, lineHeight: 1.2 }}>{tr("guideMe", "Guide me through this card")}</span>
              <span style={{ display: "block", fontSize: 10.5, color: P.faded, lineHeight: 1.3 }}>{tr("guideSub", "Dignity reads the numbers out, in order")}</span>
            </span>
          </button>
          {!briefing && (
            <div style={{ display: "flex", alignItems: "center", gap: 6, padding: "8px 14px 10px", fontSize: 10.5, color: P.faded, lineHeight: 1.35 }}>
              <Video style={{ width: 12, height: 12, flexShrink: 0 }} />
              {tr("videoSoon", "A recorded briefing on this hospital, dated to the CMS release, lands here when produced.")}
            </div>
          )}
          {/* How to read the card — the three scales it mixes */}
          <div style={{ padding: "10px 14px 12px", borderTop: `1px solid ${P.divider}`, display: "flex", flexDirection: "column", gap: 7, fontSize: 11, lineHeight: 1.35, color: P.muted }}>
            <div style={{ fontSize: 9.5, fontWeight: 800, letterSpacing: "0.12em", textTransform: "uppercase", color: P.goldInk }}>{tr("legend.header", "How to read this card")}</div>
            <div style={{ display: "flex", gap: 8, alignItems: "flex-start" }}>
              <StarRow value={4} size={10} muted={P.faded} />
              <span>{tr("legend.stars", "Stars are Medicare's (CMS) and patients' (HCAHPS), 1–5. More is better.")}</span>
            </div>
            <div style={{ display: "flex", gap: 8, alignItems: "flex-start" }}>
              <span style={{ display: "inline-flex", gap: 2, marginTop: 2, flexShrink: 0 }} aria-hidden>
                <span style={{ width: 9, height: 9, borderRadius: 2, background: P.tones.good.bar }} />
                <span style={{ width: 9, height: 9, borderRadius: 2, background: P.tones.neutral.bar }} />
                <span style={{ width: 9, height: 9, borderRadius: 2, background: P.tones.bad.bar }} />
              </span>
              <span>{tr("legend.verdicts", "Green is better than the national benchmark, gold about the same, red worse — CMS's own verdicts.")}</span>
            </div>
            <div style={{ display: "flex", gap: 8, alignItems: "flex-start" }}>
              <span style={{ fontFamily: mono, fontSize: 10, marginTop: 1, color: P.goldInk, flexShrink: 0 }}>1.00</span>
              <span>{tr("legend.sir", "Infection ratios compare infections seen to infections expected. Under 1.00 is fewer than expected.")}</span>
            </div>
            <div style={{ display: "flex", gap: 8, alignItems: "flex-start" }}>
              <span style={{ display: "inline-flex", width: 14, height: 14, borderRadius: 3, alignItems: "center", justifyContent: "center", background: "#ca8a04", color: "#fff", fontSize: 9, fontWeight: 900, flexShrink: 0, marginTop: 1 }}>C</span>
              <span>{tr("legend.grade", "The price grade is ours, from the hospital's published charges — not a quality score.")}</span>
            </div>
          </div>
          {narration && (
            <div style={{ marginTop: "auto", padding: "10px 12px 12px", borderTop: `1px solid ${P.divider}` }}>
              {narration}
            </div>
          )}
        </aside>

        {/* ── The card ─────────────────────────────────────────────────── */}
        <div style={{ padding: "12px 18px 18px", minWidth: 0 }}>
          <h3 style={{ fontFamily: serif, fontWeight: 400, fontSize: 24, lineHeight: 1.15, margin: 0, color: P.text }}>
            {tr("title", "{{name}} — quality report card", { name: displayName })}
          </h3>
          <p style={{ fontFamily: serif, fontSize: 16.5, lineHeight: 1.55, margin: "8px 0 0", color: P.text, opacity: 0.95 }}>
            {summary}
          </p>

          <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6" style={{ gap: 8, marginTop: 14 }}>
            <Tile
              icon={<Activity style={{ width: 12, height: 12 }} />}
              label={tr("tiles.cms", "CMS overall")}
              value={cmsStars != null ? cmsStars : <span style={{ fontSize: 15, lineHeight: 1.3 }}>{tr("notPublished", "CMS has not published this")}</span>}
              unit={cmsStars != null ? tr("tiles.of5", "of 5 stars") : undefined}
              sub={cmsStars != null ? <StarRow value={cmsStars} muted={P.faded} /> : tr("tiles.cmsAbsent", "No overall star this release")}
              tone={cmsStars == null ? "na" : cmsStars >= 4 ? "good" : cmsStars <= 2 ? "bad" : "neutral"}
              title={tr("tiles.cmsTitle", "Medicare's overall star rating, rolling 47 measures into one score")}
            />
            <Tile
              icon={<Heart style={{ width: 12, height: 12 }} />}
              label={tr("tiles.recommend", "Would recommend")}
              value={snap.recommendPct != null ? `${snap.recommendPct}%` : <span style={{ fontSize: 15, lineHeight: 1.3 }}>{tr("notPublished", "CMS has not published this")}</span>}
              sub={snap.surveys != null ? tr("tiles.surveys", "{{n}} HCAHPS surveys · {{rate}}% response", { n: snap.surveys.toLocaleString(), rate: snap.responseRate ?? "—" }) : tr("tiles.hcahpsNone", "No HCAHPS survey on file")}
              tone={snap.recommendPct == null ? "na" : snap.recommendPct >= 75 ? "good" : snap.recommendPct < 60 ? "bad" : "neutral"}
              title={tr("tiles.recommendTitle", "Patients who said YES, they would definitely recommend the hospital (CMS HCAHPS survey)")}
            />
            <Tile
              icon={<Users style={{ width: 12, height: 12 }} />}
              label={tr("tiles.patientRating", "Patient rating")}
              value={snap.summaryStar != null ? snap.summaryStar : <span style={{ fontSize: 15, lineHeight: 1.3 }}>{tr("notPublished", "CMS has not published this")}</span>}
              unit={snap.summaryStar != null ? tr("tiles.of5", "of 5 stars") : undefined}
              sub={snap.summaryStar == null ? tr("tiles.hcahpsNone", "No HCAHPS survey on file")
                : snap.rating910Pct != null
                  ? tr("tiles.rated910n", "{{pct}}% rated it 9 or 10 · {{n}} surveys", { pct: snap.rating910Pct, n: (snap.surveys ?? 0).toLocaleString() })
                  : tr("tiles.surveysOnly", "{{n}} surveys", { n: (snap.surveys ?? 0).toLocaleString() })}
              tone={snap.summaryStar == null ? "na" : snap.summaryStar >= 4 ? "good" : snap.summaryStar <= 2 ? "bad" : "neutral"}
              title={tr("tiles.patientRatingTitle", "HCAHPS summary star rating — patients' own account of their stay")}
            />
            <Tile
              icon={<Clock style={{ width: 12, height: 12 }} />}
              label={tr("tiles.erWait", "ER wait")}
              value={snap.erWaitMin != null ? Math.round(snap.erWaitMin) : <span style={{ fontSize: 15, lineHeight: 1.3 }}>{tr("notPublished", "CMS has not published this")}</span>}
              unit={snap.erWaitMin != null ? tr("tiles.minutes", "min median") : undefined}
              sub={erNational != null ? tr("tiles.erNational", "U.S. median {{n}} min{{left}}", { n: Math.round(erNational), left: snap.leftBeforeSeenPct != null ? ` · ${snap.leftBeforeSeenPct}% left before being seen` : "" }) : tr("tiles.erSub", "Arrival to departure, median patient")}
              tone={erTone}
              title={tr("tiles.erTitle", "CMS OP-18b: median minutes from ER arrival to departure. Lower is better.")}
            />
            <Tile
              icon={<Star style={{ width: 12, height: 12 }} />}
              label={tr("tiles.google", "Google")}
              value={google != null ? google.toFixed(1) : <span style={{ fontSize: 15, lineHeight: 1.3 }}>{tr("tiles.googleAbsent", "No Google rating on file")}</span>}
              unit={google != null ? "★" : undefined}
              sub={googleN != null ? tr("tiles.reviews", "{{n}} reviews", { n: googleN.toLocaleString() }) : tr("tiles.googleNone", "No Google reviews found")}
              tone={google == null ? "na" : google >= 4 ? "good" : google < 3 ? "bad" : "neutral"}
              title={tr("tiles.googleTitle", "Patient-submitted Google reviews, refreshed via the Google Places API")}
            />
            <Tile
              icon={<Banknote style={{ width: 12, height: 12 }} />}
              label={tr("tiles.price", "Price grade")}
              value={gradeLetter ? (
                <span style={{ display: "inline-flex", width: 34, height: 34, borderRadius: 7, alignItems: "center", justifyContent: "center", background: gi!.color, color: "#fff", fontSize: 20, fontWeight: 900, verticalAlign: "middle", boxShadow: "0 1px 2px rgba(0,0,0,0.3)" }}>{gradeLetter}</span>
              ) : "—"}
              unit={price.markup != null ? `${price.markup >= 0 ? "+" : "−"}${Math.abs(Math.round(price.markup)).toLocaleString()}%` : undefined}
              sub={price.rank != null && price.totalRanked ? tr("tiles.ranked", "#{{rank}} of {{total}} · vs national median", { rank: price.rank.toLocaleString(), total: price.totalRanked.toLocaleString() }) : tr("tiles.priceSub", "Published charges vs national median")}
              tone={gradeLetter === "A" || gradeLetter === "B" ? "good" : gradeLetter === "F" || gradeLetter === "D" ? "bad" : gradeLetter ? "neutral" : "na"}
              title={tr("tiles.priceTitle", "Our A–F price grade from the hospital's own published chargemaster — a separate scale from quality")}
            />
          </div>

          {/* CMS domains + infections + timeliness at a glance */}
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 12 }}>
            <Chip label={tr("chips.mortality", "Mortality")} tone={toneOf(hospital.cms_mortality_rating)} value={domainLabel(hospital.cms_mortality_rating)} />
            <Chip label={tr("chips.safety", "Safety of care")} tone={toneOf(hospital.cms_safety_rating)} value={domainLabel(hospital.cms_safety_rating)} />
            <Chip label={tr("chips.readmission", "Readmissions")} tone={toneOf(hospital.cms_readmission_rating)} value={domainLabel(hospital.cms_readmission_rating)} />
            <Chip
              label={tr("chips.infections", "Infections")}
              tone={snap.hai.length === 0 ? "na" : haiTally.bad > 0 ? "bad" : haiTally.good > 0 ? "good" : "neutral"}
              value={snap.hai.length === 0 ? tr("notPublished", "CMS has not published this") : tr("chips.haiValue", "{{better}} better · {{same}} same · {{worse}} worse", { better: haiTally.good, same: haiTally.neutral, worse: haiTally.bad })}
            />
            <Chip
              label={tr("chips.timeliness", "Timely care")}
              tone={erTone}
              value={snap.erWaitMin == null ? tr("notPublished", "CMS has not published this") : erTone === "good" ? tr("chips.erFaster", "Faster ER than U.S. median") : erTone === "bad" ? tr("chips.erSlower", "Slower ER than U.S. median") : tr("chips.erTypical", "Typical ER wait")}
            />
          </div>

          {/* Verdict tallies across every reported measure + HCAHPS domains */}
          <div className="grid grid-cols-1 lg:grid-cols-2" style={{ gap: 16, marginTop: 16, paddingTop: 14, borderTop: `1px solid ${P.divider}` }}>
            <div>
              <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 8, marginBottom: 8 }}>
                <span style={{ fontSize: 10.5, fontWeight: 800, letterSpacing: "0.12em", textTransform: "uppercase", color: P.muted }}>
                  <Stethoscope style={{ width: 12, height: 12, display: "inline", verticalAlign: "-2px", marginRight: 5, color: P.goldInk }} />
                  {tr("tally.header", "Care Compare measures")}
                </span>
                <span style={{ fontFamily: mono, fontSize: 11, color: P.faded }}>
                  {measuresLoading ? tr("tally.loading", "loading…") : tr("tally.counts", "{{n}} with a verdict", { n: measuresCounted })}
                </span>
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                {tallies.map(tallyBar)}
                {!measuresLoading && measuresCounted === 0 && (
                  <div style={{ fontSize: 12, color: P.faded }}>{tr("tally.none", "CMS has not published comparison verdicts for this hospital.")}</div>
                )}
              </div>
              <div style={{ display: "flex", gap: 10, marginTop: 8, fontSize: 10.5, color: P.faded }}>
                <span><span style={{ display: "inline-block", width: 8, height: 8, borderRadius: 2, background: P.tones.good.bar, marginRight: 4 }} />{tr("tally.better", "better than national")}</span>
                <span><span style={{ display: "inline-block", width: 8, height: 8, borderRadius: 2, background: P.tones.neutral.bar, marginRight: 4 }} />{tr("tally.same", "about the same")}</span>
                <span><span style={{ display: "inline-block", width: 8, height: 8, borderRadius: 2, background: P.tones.bad.bar, marginRight: 4 }} />{tr("tally.worse", "worse")}</span>
              </div>
            </div>
            <div>
              <div style={{ fontSize: 10.5, fontWeight: 800, letterSpacing: "0.12em", textTransform: "uppercase", color: P.muted, marginBottom: 8 }}>
                <Users style={{ width: 12, height: 12, display: "inline", verticalAlign: "-2px", marginRight: 5, color: P.goldInk }} />
                {tr("domains.header", "What patients said (HCAHPS)")}
                {snap.surveys != null && (
                  <span style={{ fontFamily: mono, fontWeight: 500, letterSpacing: 0, textTransform: "none", marginLeft: 8 }}>
                    {tr("domains.surveys", "{{n}} completed surveys", { n: snap.surveys.toLocaleString() })}
                  </span>
                )}
              </div>
              {snap.domains.length ? (
                <div className="grid grid-cols-1 sm:grid-cols-2" style={{ gap: "4px 14px" }}>
                  {snap.domains.map((d) => (
                    <div key={d.key} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, fontSize: 12 }}>
                      <span style={{ color: P.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{tr(`domains.${d.key}`, d.label)}</span>
                      <StarRow value={d.stars} size={12} muted={P.faded} />
                    </div>
                  ))}
                </div>
              ) : (
                <div style={{ fontSize: 12, color: P.faded }}>{tr("domains.none", "No HCAHPS domain ratings on file for this hospital.")}</div>
              )}
              {snap.hai.length > 0 && (
                <div style={{ marginTop: 10 }}>
                  <div style={{ fontSize: 10.5, fontWeight: 800, letterSpacing: "0.12em", textTransform: "uppercase", color: P.muted, marginBottom: 6 }}>
                    <AlertTriangle style={{ width: 12, height: 12, display: "inline", verticalAlign: "-2px", marginRight: 5, color: P.goldInk }} />
                    {tr("hai.header", "Infection ratios (SIR · 1.0 = expected)")}
                  </div>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 5 }}>
                    {snap.hai.map((h) => {
                      const c = P.tones[toneOf(h.cmp)];
                      return (
                        <span key={h.code} title={h.name} style={{ fontFamily: mono, fontSize: 11, padding: "3px 8px", borderRadius: 999, background: c.bg, border: `1px solid ${c.bd}`, color: c.fg, whiteSpace: "nowrap" }}>
                          {tr(`hai.${h.code}`, h.label)} {h.sir!.toFixed(2)}
                        </span>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
